import React, { useState } from 'react';
import { MessageCircle, Bug, Lightbulb, MessageSquare, X } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useToast } from './ui/Toast';
import { submitFeedback } from '../lib/sync/api-client';
import './FeedbackWidget.css';

type FeedbackType = 'bug' | 'feature' | 'general';

export default function FeedbackWidget() {
  const { session } = useAuthStore();
  const { showToast } = useToast();
  
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Do not show widget if not authenticated
  if (!session?.uid) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    
    try {
      await submitFeedback({
        type,
        message,
        metadata: {
          userAgent: navigator.userAgent,
          route: window.location.pathname,
          viewportSize: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString(),
          appVersion: import.meta.env.VITE_APP_VERSION || '0.1.0-beta'
        }
      });
      
      showToast('Thank you for your feedback!', 'success');
      setIsOpen(false);
      setMessage('');
      setType('general');
    } catch {
      showToast('Failed to send feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* ── Edge tab trigger (middle-right) ─── */}
      {!isOpen && (
        <button
          className="feedback-edge-tab"
          onClick={() => setIsOpen(true)}
          aria-label="Send feedback"
        >
          <MessageCircle size={14} />
          Feedback
        </button>
      )}

      {/* ── Bottom sheet modal ─── */}
      {isOpen && (
        <div className="feedback-backdrop" onClick={handleClose}>
          <div
            className="feedback-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-handle" />

            <div className="feedback-header">
              <h3>Send Feedback</h3>
              <button className="feedback-close" onClick={handleClose}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="feedback-types">
                <button
                  type="button"
                  className={`feedback-type-btn ${type === 'bug' ? 'active' : ''}`}
                  onClick={() => setType('bug')}
                >
                  <Bug size={16} /> Bug
                </button>
                <button
                  type="button"
                  className={`feedback-type-btn ${type === 'feature' ? 'active' : ''}`}
                  onClick={() => setType('feature')}
                >
                  <Lightbulb size={16} /> Feature
                </button>
                <button
                  type="button"
                  className={`feedback-type-btn ${type === 'general' ? 'active' : ''}`}
                  onClick={() => setType('general')}
                >
                  <MessageSquare size={16} /> General
                </button>
              </div>
              
              <textarea
                className="input feedback-textarea"
                placeholder="Tell us what's on your mind..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                required
                autoFocus
              />
              
              <button 
                type="submit" 
                className="btn btn--primary btn--sm feedback-submit"
                disabled={isSubmitting || !message.trim()}
              >
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
